// frontend/src/components/MetaCubeOracleV2.jsx
import React, { useRef, useState, useEffect } from "react";
import * as THREE from "three";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { EffectComposer, Bloom } from "@react-three/postprocessing";

// --- Forcer la caméra à regarder le centre ---
function CameraController() {
  const { camera } = useThree();
  useFrame(() => camera.lookAt(0, 0, 0));
  return null;
}

function MetaCubeOracleV2Core({
  running = true,
  chaos = false,
  glow = true,
  cubeSpeedPct = 100,
  ringSpeedPct = 100,
  colorShiftRatePct = 50,
  surfaceSpeedPct = 50,
  bloomIntensity = 1.3,
  bloomRadius = 0.7,
  bloomThreshold = 0.0,
  onEntropyUpdate = () => {},
}) {
  const groupRef = useRef();
  const [time, setTime] = useState(0);
  const [entropyDyn, setEntropyDyn] = useState(0);

  // --- Entropie dynamique non bornée via backend ---
  useEffect(() => {
    if (!running) return;

    const interval = setInterval(async () => {
      try {
        const res = await fetch("/api/geometry/metacube_oracle_v2/dyn_entropy", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            rotation_speed: cubeSpeedPct / 100,
            turbulence: Math.random() * 1.2,
            resonance: Math.random() * 1.5,
            chaos_factor: chaos ? 1.5 + Math.random() : 0.9,
            glow,
          }),
        });

        const data = await res.json();
        if (typeof data.entropy_dyn === "number") {
          setEntropyDyn((prev) =>
            prev === 0 ? data.entropy_dyn : prev + (data.entropy_dyn - prev) * 0.3
          );
          onEntropyUpdate(data.entropy_dyn);
        }
      } catch (err) {
        console.warn("Erreur dyn_entropy MetaCubeOracleV2:", err);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [running, chaos, glow, cubeSpeedPct, onEntropyUpdate]);

  // --- Animation visuelle ---
  useFrame((_, delta) => {
    if (!running || !groupRef.current) return;
    setTime((t) => t + delta);
    const g = groupRef.current;

    const cube = g.getObjectByName("cinematicCube");
    if (cube) {
      const speedFactor = cubeSpeedPct / 100;
      cube.rotation.x += delta * 2.0 * speedFactor;
      cube.rotation.y += delta * 1.6 * speedFactor;
      cube.rotation.z += delta * 1.2 * speedFactor;
    }

    const halo = g.getObjectByName("cubeHalo");
    if (halo) {
      const shiftFactor = colorShiftRatePct / 100;
      const dynamicScale =
        1.0 +
        Math.sin(time * (6 + shiftFactor * 10)) * 0.2 +
        entropyDyn * 0.01; // Entropie influence la pulsation
      halo.scale.setScalar(dynamicScale);
    }

    for (let i = 0; i < 5; i++) {
      const ring = g.getObjectByName(`data-ring-${i}`);
      if (ring) {
        const speedFactor = ringSpeedPct / 100;
        ring.rotation.x += delta * (1.0 + i * 0.25) * speedFactor;
        ring.rotation.y += delta * (0.8 + i * 0.2) * speedFactor;
      }
    }

    const surfaces = g.children.filter((obj) => obj.name?.startsWith("floating-surface"));
    const surfaceFactor = surfaceSpeedPct / 10;
    surfaces.forEach((surface, idx) => {
      surface.rotation.x += delta * 1.0 * surfaceFactor;
      surface.rotation.y += delta * 0.8 * surfaceFactor;

      const orbitRadius = 25;
      const orbitSpeed = 0.5 + idx * 0.2;
      const angle = time * orbitSpeed * surfaceFactor + (idx * Math.PI * 2) / surfaces.length;
      surface.position.x = Math.cos(angle) * orbitRadius;
      surface.position.z = Math.sin(angle) * orbitRadius;
    });

    if (chaos) {
      g.position.x = Math.sin(time * 3) * 0.5;
      g.position.y = Math.cos(time * 2) * 0.5;
    } else {
      g.position.set(0, 0, 0);
    }
  });

  return (
    <>
      <group ref={groupRef}>
        {/* Cube central */}
        <mesh name="cinematicCube">
          <boxGeometry args={[12, 12, 12]} />
          <meshPhongMaterial
            color={new THREE.Color().setHSL(0.6, 0.7, 0.5)}
            emissive={glow ? new THREE.Color(0x2288ff) : new THREE.Color(0x000000)}
            shininess={160}
            transparent
            opacity={0.95}
          />
        </mesh>

        {/* Halo énergétique */}
        <mesh name="cubeHalo">
          <sphereGeometry args={[14, 14, 14]} />
          <meshBasicMaterial color={0x00aaff} transparent opacity={0.2} side={THREE.BackSide} />
        </mesh>

        {/* Anneaux orbitaux */}
        {[0, 1, 2, 3, 4].map((i) => (
          <mesh key={i} name={`data-ring-${i}`}>
            <torusGeometry args={[18 + i * 3, 0.3, 16, 100]} />
            <meshBasicMaterial
              color={new THREE.Color().setHSL(i / 5, 0.8, 0.6)}
              transparent
              opacity={0.5}
            />
          </mesh>
        ))}

        {/* Surfaces flottantes */}
        {[
          { pos: [25, 0, 0], rot: [0, Math.PI / 2, 0], color: 0xffa500 },
          { pos: [-25, 0, 0], rot: [0, -Math.PI / 2, 0], color: 0x9400d3 },
          { pos: [0, 25, 0], rot: [Math.PI / 2, 0, 0], color: 0x32cd32 },
        ].map((cfg, i) => (
          <mesh key={i} name={`floating-surface-${i}`} position={cfg.pos} rotation={cfg.rot}>
            <planeGeometry args={[16, 16]} />
            <meshPhongMaterial
              color={cfg.color}
              emissive={new THREE.Color(cfg.color).multiplyScalar(glow ? 0.4 : 0)}
              transparent
              opacity={0.85}
              side={THREE.DoubleSide}
            />
          </mesh>
        ))}
      </group>

      {/* Effets post-processing avec sliders Bloom */}
      <EffectComposer>
        <Bloom intensity={bloomIntensity} radius={bloomRadius} threshold={bloomThreshold} />
      </EffectComposer>
    </>
  );
}

export default function MetaCubeOracleV2(props) {
  return (
    <div className="visualizer-container">
      <Canvas camera={{ position: [0, 20, 40], fov: 60 }}>
        <ambientLight intensity={0.6} />
        <pointLight position={[20, 20, 20]} intensity={1.2} />
        <MetaCubeOracleV2Core {...props} />
        <OrbitControls enableZoom enablePan />
        <CameraController />
      </Canvas>
    </div>
  );
}
